import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PI } from '../../constants-types';
import {
    CompanySettingsServerResponse,
    PersonalizationSettingsRequest,
    PersonalizationSettingsResponse,
    PersonalizationSettingsServerResponse,
    SoftwareModuleAccessConfiguration
} from '../../core-data-model';
import {
    CompanySettings,
    CompanySettingsUpdate
} from '../../core-data-model/CompanySettings.model';
import { CompanySettingsServer } from '../../core-data-model/CompanySettingsApi.serverinterface';
import { EmailSettingsRequest } from '../../core-data-model/EmailSettings/email-settings-request.interface';
import { EmailSettingsResponse } from '../../core-data-model/EmailSettings/email-settings-response.interface';
import { ApiService } from '../../system-services/Api.service';
import { UserProfileSettingsService } from '../../system-services/UserProfileSettings/UserProfileSettings.service';
import { CompanySettingsApiTransformer } from './CompanySettingsApi.datatransform';

@Injectable({ providedIn: 'root' })
export class CompanySettingsApi {
    private readonly companyGuid: string = null;

    constructor(
        private api: ApiService,
        private http: HttpClient,
        private userProfile: UserProfileSettingsService,
        private transform: CompanySettingsApiTransformer
    ) {
        this.companyGuid = this.userProfile.currentCompany;
    }

    /**
     * Fetches company settings for the current company.
     * The company ID is determined based on the user's current company.
     * This is deprecated and should only be used for legacy purposes.
     *  In PI2, the v5 API should be used instead.
     *
     * @returns A promise that resolves to CompanySettings.
     */
    getCompanySettings(): Promise<CompanySettings> {
        return this.api
            .get<CompanySettingsServer>(
                `/api/v4/companies/${this.userProfile.currentCompany}/companysettings`
            )
            .then(([response]) =>
                this.transform.transformServerToClient(response)
            );
    }

    patchCompanySettings(
        settings: CompanySettingsUpdate
    ): Promise<CompanySettings> {
        return this.api
            .patch(
                `/api/v4/companies/${this.userProfile.currentCompany}/companysettings`,
                this.transform.transformClientToServer(settings)
            )
            .then(([response]) =>
                this.transform.transformServerToClient(response)
            );
    }

    getCompanySoftwareModuleAccessConfiguration(): Promise<SoftwareModuleAccessConfiguration> {
        return this.api
            .get<SoftwareModuleAccessConfiguration>(
                `/api/v4/companies/${this.userProfile.currentCompany}/companysettings/softwaremoduleaccessconfig`
            )
            .then(([response]) => response);
    }

    fetchEmailSettings(
        emailSettingsRequest: EmailSettingsRequest
    ): Observable<EmailSettingsResponse> {
        return this.http.get<EmailSettingsResponse>(
            this.companySettingsApiEndpoint(
                `emailsettings/${emailSettingsRequest.emailType}`
            )
        );
    }

    fetchPersonalizationSettings(): Observable<PersonalizationSettingsResponse> {
        return this.http
            .get<PersonalizationSettingsServerResponse>(
                this.companySettingsApiEndpoint('companysettings')
            )
            .pipe(map((r) => r.data));
    }

    /**
     * Fetches core company settings for PI2 companies
     */
    getCompanySettingsForPI2(): Observable<CompanySettings> {
        return this.http
            .get<CompanySettingsServerResponse>(
                this.companySettingsApiEndpoint('companysettings')
            )
            .pipe(map((r) => r.data));
    }

    updatePersonalizationSettings(
        request: PersonalizationSettingsRequest
    ): Observable<PersonalizationSettingsRequest> {
        return this.http.put<PersonalizationSettingsRequest>(
            this.companySettingsApiEndpoint('companysettings'),
            request
        );
    }

    /**
     * This method is used to get company settings for the current company.
     * In PI2, the v5 API should be used instead.
     */
    getCompanySettingsV5(): Observable<CompanySettings> {
        return this.http
            .get<CompanySettingsServer>(
                this.companySettingsApiEndpoint('legacycompanysettings')
            )
            .pipe(
                map((response) =>
                    this.transform.transformServerToClient(response)
                )
            );
    }

    private companySettingsApiEndpoint(
        endpoint: string,
        withCompanyId = true
    ): string {
        let baseApi =
            PI?.appSettings?.companySettingsApiUrl?.concat('/api/v5/') ?? '';
        baseApi = baseApi.replace(/\/$/, '').concat('/');
        endpoint = endpoint.replace(/(^\/{0,2})|(\/{0,2}$)/g, '');

        return withCompanyId
            ? baseApi.concat(`companies/${this.companyGuid}/${endpoint}`)
            : baseApi.concat(endpoint);
    }
}
